import { prefersReducedMotion } from '../utils/media.js';
import { animatePageHeaderAppear } from '../utils/page-header-appear.js';
import { anchorFromEventTarget, bindTransitionSpeculation } from '../utils/speculation.js';

let gsapPromise = null;

function loadGsap() {
	if (!gsapPromise) {
		gsapPromise = import('../gsap-config.js').then((module) => module.gsap);
	}
	return gsapPromise;
}

const EXIT_OVERLAY_MS = 0.38;
const EXIT_LOGO_MS = 0.28;
const ENTER_OVERLAY_MS = 0.55;
const ENTER_LOGO_MS = 0.32;
const ENTER_LOGO_HOLD_S = 0.5;
const ENTER_STAGGER = 0.1;
const FALLBACK_MS = 3000;
const SESSION_LOGO_ENTER_KEY = 'tt-session-logo-enter';

let initialized = false;
let enterStarted = false;
let enterFinished = false;
let userInterruptedEnterScroll = false;

function markEnterScrollInterrupt() {
	userInterruptedEnterScroll = true;
}

function hasSeenSessionLogoEnter() {
	try {
		return sessionStorage.getItem(SESSION_LOGO_ENTER_KEY) === '1';
	} catch {
		return false;
	}
}

function markSessionLogoEnter() {
	try {
		sessionStorage.setItem(SESSION_LOGO_ENTER_KEY, '1');
		document.documentElement.classList.add('tt-skip-logo-enter');
	} catch {
		// sessionStorage unavailable — logo may replay on later navigations
	}
}

function shouldPlayLogoEnter() {
	return !hasSeenSessionLogoEnter();
}

function whenPageActive(callback) {
	if (document.prerendering) {
		document.addEventListener('prerenderingchange', callback, { once: true });
		return;
	}
	callback();
}

function supportsCrossDocumentViewTransitions() {
	return CSS.supports('view-transition-name', 'none')
		&& 'onpagereveal' in window;
}

const MORPH_ITEM_SELECTORS = [
	'.portfolio-interactive-item',
	'.blog-list-item',
	'.tt-grid-item',
	'[data-orbit-tiles-item]',
	'[data-radial-slider-item]',
].join(', ');

function morphNameMatchesHref(morphName, href) {
	try {
		const { pathname } = new URL(href, window.location.href);

		if (morphName.startsWith('vt-project-')) {
			const slug = pathname.match(/^\/projects\/([^/]+)\/?$/)?.[1];
			return Boolean(slug && morphName === `vt-project-${slug}`);
		}

		if (morphName.startsWith('vt-blog-')) {
			const slug = pathname.match(/^\/blog\/post\/([^/]+)\/?$/)?.[1];
			return Boolean(slug && morphName === `vt-blog-${slug}`);
		}
	} catch {
		return false;
	}

	return false;
}

function findMorphElement(link) {
	const inLink = link.querySelector('[data-vt-morph]');
	if (inLink) return inLink;

	const item = link.closest(MORPH_ITEM_SELECTORS);
	return item?.querySelector('[data-vt-morph]') ?? null;
}

function clearListMorphNames() {
	document.querySelectorAll('[data-vt-morph]').forEach((el) => {
		el.style.viewTransitionName = 'none';
	});
}

function prepareMorphForLink(link) {
	const morphEl = findMorphElement(link);
	const morphName = morphEl?.dataset.vtMorph;

	if (!morphName || !morphNameMatchesHref(morphName, link.href)) {
		return null;
	}

	clearListMorphNames();
	morphEl.style.viewTransitionName = morphName;
	return morphName;
}

function getOverlay() {
	return document.querySelector('.ptr-overlay');
}

function getLogo() {
	return document.querySelector('.ptr-preloader');
}

function getTransitionRoot() {
	return document.getElementById('page-transition');
}

async function finishEnter() {
	if (enterFinished) return;
	enterFinished = true;

	const root = getTransitionRoot();
	if (root) {
		const gsap = await loadGsap();
		gsap.set(root, { display: 'none', autoAlpha: 0, pointerEvents: 'none' });
		gsap.set([getOverlay(), getLogo()].filter(Boolean), { clearProps: 'all' });
	}

	await animatePageHeaderAppear(
		() => {
			document.documentElement.classList.remove('tt-vt-navigation');
			document.body.classList.add('tt-transition-ready');
		},
		() => {
			window.dispatchEvent(new CustomEvent('tt:transition-complete'));
		},
	);
}

function skipEnterImmediately() {
	enterStarted = true;
	finishEnter();
}

async function enterPage() {
	if (enterStarted || !document.body.classList.contains('tt-transition')) {
		document.body.classList.add('tt-transition-ready');
		return;
	}

	enterStarted = true;

	const root = getTransitionRoot();
	if (!root) {
		markSessionLogoEnter();
		finishEnter();
		return;
	}

	if (prefersReducedMotion()) {
		markSessionLogoEnter();
		finishEnter();
		return;
	}

	if (!shouldPlayLogoEnter()) {
		finishEnter();
		return;
	}

	const gsap = await loadGsap();

	gsap.set(root, { display: 'block', autoAlpha: 1, pointerEvents: 'none' });
	gsap.set(getOverlay(), { autoAlpha: 1 });
	gsap.set(getLogo(), { autoAlpha: 1, y: 0 });

	const tl = gsap.timeline({
		defaults: { ease: 'power2.inOut' },
		onComplete: () => {
			clearTimeout(fallbackId);
			markSessionLogoEnter();
			finishEnter();
		},
	});

	tl.to(getLogo(), {
		autoAlpha: 0,
		y: -10,
		duration: ENTER_LOGO_MS,
	}, ENTER_LOGO_HOLD_S);
	tl.to(getOverlay(), {
		autoAlpha: 0,
		duration: ENTER_OVERLAY_MS,
	}, ENTER_LOGO_HOLD_S + ENTER_STAGGER);

	const fallbackId = setTimeout(() => {
		markSessionLogoEnter();
		finishEnter();
	}, FALLBACK_MS);
}

async function exitPage(url) {
	if (hasSeenSessionLogoEnter()) {
		window.location.href = url;
		return;
	}

	const root = getTransitionRoot();

	if (!root || prefersReducedMotion()) {
		window.location.href = url;
		return;
	}

	const gsap = await loadGsap();

	gsap.set(root, { display: 'block', autoAlpha: 1, pointerEvents: 'auto' });
	gsap.set(getOverlay(), { autoAlpha: 0 });
	gsap.set(getLogo(), { autoAlpha: 0, y: 12 });

	const tl = gsap.timeline({
		defaults: { ease: 'power2.inOut' },
		onComplete: () => {
			window.location.href = url;
		},
	});

	tl.to(getLogo(), {
		autoAlpha: 1,
		y: 0,
		duration: EXIT_LOGO_MS,
	}, 0);
	tl.to(getOverlay(), {
		autoAlpha: 1,
		duration: EXIT_OVERLAY_MS,
	}, 0.08);
}

function isTransitionHref(link) {
	if (!link?.matches?.('a[href]')) return false;
	if (link.matches('[target="_blank"], [href^="#"], [href^="mailto"], [href^="tel"], .tt-btn-disabled, .no-transition')) {
		return false;
	}
	if (link.hasAttribute('download')) return false;

	const href = link.getAttribute('href');
	if (!href || href === '#') return false;

	try {
		const target = new URL(link.href, window.location.href);
		if (target.origin !== window.location.origin) return false;
		if (target.pathname === window.location.pathname && target.search === window.location.search && target.hash) {
			return false;
		}
	} catch {
		return false;
	}

	return true;
}

function isTransitionLink(link, event) {
	if (event.defaultPrevented || event.button !== 0) return false;
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
	return isTransitionHref(link);
}

function bindLinkTransitions() {
	document.addEventListener('click', (event) => {
		const link = anchorFromEventTarget(event.target);
		if (!link || !isTransitionLink(link, event)) return;

		event.preventDefault();
		exitPage(link.href);
	});
}

function onPageReveal(event) {
	if (event.persisted) {
		window.location.reload();
		return;
	}

	if (event.viewTransition) {
		document.documentElement.classList.add('tt-vt-navigation');
		enterStarted = true;
		userInterruptedEnterScroll = false;
		window.addEventListener('wheel', markEnterScrollInterrupt, { passive: true, once: true });
		window.addEventListener('touchmove', markEnterScrollInterrupt, { passive: true, once: true });

		const completeViewTransitionEnter = () => {
			if (enterFinished) return;

			if (window.scrollY !== 0 && !userInterruptedEnterScroll) {
				window.scrollTo(0, 0);
			}

			requestAnimationFrame(() => {
				requestAnimationFrame(() => finishEnter());
			});
		};

		event.viewTransition.finished
			.then(completeViewTransitionEnter)
			.catch(completeViewTransitionEnter);

		return;
	}

	if (!enterStarted) {
		if (shouldPlayLogoEnter()) {
			enterPage();
		} else {
			skipEnterImmediately();
		}
	}
}

function runEnterForCurrentPage() {
	if (enterStarted) return;

	if (shouldPlayLogoEnter()) {
		enterPage();
	} else {
		skipEnterImmediately();
	}
}

function scheduleEnterFallback() {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', runEnterForCurrentPage, { once: true });
	} else {
		requestAnimationFrame(runEnterForCurrentPage);
	}
}

function initViewTransitionPath() {
	document.documentElement.classList.add('tt-view-transitions');
	clearListMorphNames();
	window.addEventListener('pagereveal', onPageReveal);

	document.addEventListener('click', (event) => {
		const link = anchorFromEventTarget(event.target);
		if (!link || !isTransitionLink(link, event)) return;

		prepareMorphForLink(link);
	}, true);

	if (!prefersReducedMotion()) {
		bindTransitionSpeculation((link, trigger) => {
			if (!isTransitionHref(link)) return false;
			if (trigger === 'hover') {
				return Boolean(findMorphElement(link));
			}
			return true;
		});
	}

	if (window.__ttPendingPageReveal) {
		const pending = window.__ttPendingPageReveal;
		window.__ttPendingPageReveal = null;
		onPageReveal(pending);
	} else {
		window.addEventListener('load', () => {
			runEnterForCurrentPage();
		}, { once: true });
	}
}

export function initPageTransitions() {
	whenPageActive(() => {
		if (initialized || !document.body.classList.contains('tt-transition')) return;
		initialized = true;

		if (supportsCrossDocumentViewTransitions() && !prefersReducedMotion()) {
			initViewTransitionPath();
		} else {
			scheduleEnterFallback();
			bindLinkTransitions();
		}

		window.addEventListener('pageshow', (event) => {
			if (event.persisted) {
				window.location.reload();
			}
		});
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initPageTransitions);
} else {
	initPageTransitions();
}
